// src/lib/season-icon.js
//
// PR #289 commit 2 — hemisphere-aware season resolution for the Year view
// header icon. Default behaviour is Northern hemisphere; users in the
// Southern hemisphere see the same months mapped to the opposite season,
// e.g. May in AU/NZ/ZA is Autumn, not Spring.
//
// Region detection plugs into the existing `tend:region` localStorage value
// (set by `detectRegion()` and the onboarding region picker). Returns
// `{ id, label, tone }` only — the consumer is responsible for picking the
// matching Lucide icon component to keep this file framework-free.

import { Sprout, Sun, TreeDeciduous, Snowflake } from 'lucide-react';

// Northern hemisphere meteorological seasons (1-indexed months).
const NORTH_BY_MONTH = {
  3: 'spring', 4: 'spring', 5: 'spring',
  6: 'summer', 7: 'summer', 8: 'summer',
  9: 'autumn', 10: 'autumn', 11: 'autumn',
  12: 'winter', 1: 'winter', 2: 'winter',
};
const SOUTH_BY_MONTH = {
  9: 'spring', 10: 'spring', 11: 'spring',
  12: 'summer', 1: 'summer', 2: 'summer',
  3: 'autumn', 4: 'autumn', 5: 'autumn',
  6: 'winter', 7: 'winter', 8: 'winter',
};

// ISO-3166 two-letter codes that sit in the Southern hemisphere. Anything
// not on this list (or 'auto'/'unknown'/null/undefined) is treated as
// Northern, including the GB / IE / DE / FR / US / CA / EU / Asia majority.
const SOUTHERN_REGIONS = new Set([
  'AU', // Australia
  'NZ', // New Zealand
  'ZA', // South Africa
  'AR', // Argentina
  'CL', // Chile
  'UY', // Uruguay
  'PY', // Paraguay
  'BO', // Bolivia
  'PE', // Peru
  'BR', // Brazil — most of the country
  'EC', // Ecuador
  // Pacific island nations south of the equator
  'FJ', 'PG',
]);

export function isSouthernHemisphere(region) {
  if (!region) return false;
  const code = String(region).toUpperCase().trim();
  if (code === 'AUTO' || code === 'UNKNOWN') return false;
  return SOUTHERN_REGIONS.has(code);
}

const SEASON_DETAIL = {
  spring: { label: 'Spring', tone: '#c8e0a8', icon: Sprout },
  summer: { label: 'Summer', tone: '#e8d068', icon: Sun },
  autumn: { label: 'Autumn', tone: '#d08858', icon: TreeDeciduous },
  winter: { label: 'Winter', tone: '#d0dae0', icon: Snowflake },
};

// Returns { id, label, tone, Icon } for the supplied month + region.
// Defaults: month = current month (1-12), region = null (Northern).
export function getSeasonIcon(month, region) {
  const m = (month >= 1 && month <= 12) ? month : new Date().getMonth() + 1;
  const map = isSouthernHemisphere(region) ? SOUTH_BY_MONTH : NORTH_BY_MONTH;
  const id = map[m] || 'spring';
  const detail = SEASON_DETAIL[id];
  return { id, label: detail.label, tone: detail.tone, Icon: detail.icon };
}
